import { useEffect, useMemo, useRef, useState } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { MapPinned, Loader2 } from "lucide-react";
import { authGet } from "../../utils/auth";

const W = 520;
const H = 600;
let geoPromise = null;
const loadGeo = () => (geoPromise ||= fetch("/india-states.json").then((r) => r.json()));
const slug = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const LOW = [246, 231, 184];
const HIGH = [168, 118, 8];

const fillFor = (count, max) => {
  if (!count) return "#E6EAF2";
  const t = max > 1 ? Math.sqrt((count - 1) / (max - 1)) : 1;
  return `rgb(${LOW.map((v, i) => Math.round(v + (HIGH[i] - v) * t)).join(",")})`;
};

const Tooltip = ({ hover, data }) => {
  if (!hover) return null;
  const cities = (data?.cities || []).filter((c) => c.state === hover.name).slice(0, 4);
  const count = hover.kind === "state" ? (data?.states || []).find((s) => s.state === hover.name)?.count || 0 : hover.count;
  return (
    <div className="pointer-events-none absolute z-10 min-w-[150px] -translate-x-1/2 -translate-y-[calc(100%+12px)] rounded-xl bg-[#1B2A4A] px-3 py-2 text-xs text-white shadow-xl"
      style={{ left: hover.x, top: hover.y }} data-testid="gift-map-tooltip">
      <p className="font-semibold">{hover.kind === "city" ? `${hover.name}, ${hover.state}` : hover.name}</p>
      <p className="text-[#E8B830] font-bold" data-testid="gift-map-tooltip-count">{count} {count === 1 ? "client" : "clients"}</p>
      {hover.kind === "state" && cities.map((c) => (
        <p key={c.city} className="flex justify-between gap-3 text-white/80"><span>{c.city}</span><span>{c.count}</span></p>
      ))}
    </div>
  );
};

const RankList = ({ title, rows, onPick, active, testId }) => {
  const max = rows[0]?.count || 1;
  return (
    <div data-testid={testId}>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#3A4557]">{title}</p>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <button key={r.label} onClick={() => onPick?.(r.label)} disabled={!onPick}
            className={`group block w-full text-left rounded-lg px-2 py-1 transition-colors ${active === r.label ? "bg-[#1B2A4A] text-white" : onPick ? "hover:bg-[#EDF1F8]" : ""}`}>
            <span className="flex justify-between text-xs font-medium"><span className="truncate">{r.label}</span><span className="font-bold">{r.count}</span></span>
            <span className="mt-1 block h-1 rounded-full bg-[#E6EAF2]"><span className="block h-1 rounded-full bg-[#D4A017]" style={{ width: `${(r.count / max) * 100}%` }} /></span>
          </button>
        ))}
        {!rows.length && <p className="text-xs text-[#6B7489]">No matching clients</p>}
      </div>
    </div>
  );
};

export const GiftIndiaMap = ({ qs, refreshKey, selectedState, onSelectState }) => {
  const [geo, setGeo] = useState(null);
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null);
  const [polls, setPolls] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => { loadGeo().then(setGeo).catch(() => setGeo(false)); }, []);
  useEffect(() => { setPolls(0); }, [refreshKey]);
  useEffect(() => {
    let active = true;
    authGet(`/api/advisor/gifts/diwali/map?${qs}`).then((res) => { if (active && res.ok) setData(res.data); }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, polls]);
  useEffect(() => {
    if (!data?.pending || polls >= 6) return undefined;
    const t = setTimeout(() => setPolls((p) => p + 1), 4000);
    return () => clearTimeout(t);
  }, [data, polls]);

  const projection = useMemo(() => (geo ? geoMercator().fitSize([W, H], geo) : null), [geo]);
  const path = useMemo(() => (projection ? geoPath(projection) : null), [projection]);
  const stateCounts = Object.fromEntries((data?.states || []).map((s) => [s.state, s.count]));
  const max = Math.max(1, ...Object.values(stateCounts));
  const pos = (e) => { const r = boxRef.current.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const dots = (data?.cities || []).filter((c) => c.lat != null && projection);

  return (
    <div className="rounded-2xl border border-[#C9D4E6] bg-white/60 p-4" data-testid="gift-india-map">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold"><MapPinned className="h-4 w-4 text-[#A87608]" /> Client map</h2>
        <span className="text-[11px] text-[#3A4557]" data-testid="gift-map-summary">
          {data?.states?.length || 0} {data?.states?.length === 1 ? "state" : "states"} · {data?.cities?.length || 0} {data?.cities?.length === 1 ? "city" : "cities"}
        </span>
      </div>
      <div className="space-y-4">
        <div ref={boxRef} className="relative" onMouseLeave={() => setHover(null)}>
          {geo === null && <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#A87608]" /></div>}
          {path && (
            <svg viewBox={`0 0 ${W} ${H}`} className="mx-auto h-auto w-full max-w-[280px]" role="img" aria-label="India map of client locations">
              {geo.features.map((f) => {
                const name = f.properties.name;
                const active = selectedState === name;
                return (
                  <path key={name} d={path(f)} fill={fillFor(stateCounts[name] || 0, max)} stroke={active ? "#1B2A4A" : "#FFFFFF"} strokeWidth={active ? 1.8 : 0.7}
                    className="cursor-pointer transition-[fill] duration-300 hover:opacity-85" data-testid={`gift-map-state-${slug(name)}`}
                    onMouseMove={(e) => setHover({ kind: "state", name, ...pos(e) })}
                    onClick={(e) => { setHover({ kind: "state", name, ...pos(e) }); onSelectState(active ? "" : name); }} />
                );
              })}
              {dots.map((c) => {
                const [x, y] = projection([c.lng, c.lat]);
                return (
                  <circle key={`${c.city}-${c.state}`} cx={x} cy={y} r={3.5 + Math.sqrt(c.count) * 1.8} fill="#1B2A4A" fillOpacity={0.85} stroke="#FFFFFF" strokeWidth={1.2}
                    className="cursor-pointer" data-testid={`gift-map-city-${slug(c.city)}`}
                    onMouseMove={(e) => { e.stopPropagation(); setHover({ kind: "city", name: c.city, state: c.state, count: c.count, ...pos(e) }); }} />
                );
              })}
            </svg>
          )}
          <Tooltip hover={hover} data={data} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <RankList title="Top states" testId="gift-map-top-states" active={selectedState} onPick={(s) => onSelectState(selectedState === s ? "" : s)}
            rows={(data?.states || []).slice(0, 5).map((s) => ({ label: s.state, count: s.count }))} />
          <RankList title="Top cities" testId="gift-map-top-cities"
            rows={(data?.cities || []).slice(0, 5).map((c) => ({ label: c.city, count: c.count }))} />
        </div>
      </div>
      <p className="mt-3 text-[10px] leading-snug text-[#6B7489]">
        Tap a state to filter the list. City locations © OpenStreetMap contributors.
        {data?.pending > 0 && <span data-testid="gift-map-pending"> Placing {data.pending} more {data.pending === 1 ? "city" : "cities"} on the map…</span>}
      </p>
    </div>
  );
};
